import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ApplicationFormService } from '@/integrations/supabase/services';
import { ApplicationForm } from '@/integrations/firebase/types';
import { useNavigate } from 'react-router-dom';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { DashboardLayout } from '@/components/DashboardLayout';

const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    purposeOfStay: '',
    plannedStayDuration: '',
    employmentStatus: '',
    monthlyIncome: '',
    accommodation: '',
    previousApplications: '',
    criminalRecord: '',
    healthInsurance: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { completionStatus, loading: statusLoading } = useCompletionStatus();

  useEffect(() => {
    if (user) {
      loadApplicationData();
    }
  }, [user]);

  const loadApplicationData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const applications = await ApplicationFormService.getByUserId(user.uid);
      
      if (applications.length > 0) {
        const data = applications[0];
        setFormData({
          purposeOfStay: data?.purpose_of_stay || '',
          plannedStayDuration: data?.planned_stay_duration?.toString() || '',
          employmentStatus: data?.employment_status || '',
          monthlyIncome: data?.monthly_income?.toString() || '',
          accommodation: data?.accommodation || '',
          previousApplications: data?.previous_applications || '',
          criminalRecord: data?.criminal_record || '',
          healthInsurance: data?.health_insurance || ''
        });
      }
    } catch (error) {
      console.error('Error loading application data:', error);
      toast({
        title: "Error loading data",
        description: "Could not load your application form data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setSubmitting(true);

    try {
      // Get existing application to check if we need to update or create
      const existingApplications = await ApplicationFormService.getByUserId(user.uid);
      const existingId = existingApplications.length > 0 ? existingApplications[0].id : null;

      await ApplicationFormService.upsert(user.uid, {
        purpose_of_stay: formData.purposeOfStay,
        planned_stay_duration: parseInt(formData.plannedStayDuration) || null,
        employment_status: formData.employmentStatus,
        monthly_income: parseFloat(formData.monthlyIncome) || null,
        accommodation: formData.accommodation,
        previous_applications: formData.previousApplications,
        criminal_record: formData.criminalRecord,
        health_insurance: formData.healthInsurance,
      }, existingId);

      toast({
        title: "Application form saved",
        description: "Your application form has been successfully updated.",
      });

      navigate('/review-application');
    } catch (error) {
      console.error('Error saving application form:', error);
      toast({
        title: "Error saving form",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Application Form">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Application Form">
      <Card>
        <CardHeader>
          <CardTitle>Application Form</CardTitle>
          <CardDescription>
            Fill out your Karta Pobytu application form details
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="purposeOfStay">Purpose of Stay</Label>
                <Select 
                  value={formData.purposeOfStay} 
                  onValueChange={(value) => handleSelectChange('purposeOfStay', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select purpose of stay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="work">Work</SelectItem>
                    <SelectItem value="study">Study</SelectItem>
                    <SelectItem value="family_reunification">Family Reunification</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="plannedStayDuration">Planned Stay Duration (months)</Label>
                <Input
                  id="plannedStayDuration"
                  name="plannedStayDuration"
                  type="number"
                  value={formData.plannedStayDuration}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="employmentStatus">Employment Status</Label>
                <Select 
                  value={formData.employmentStatus} 
                  onValueChange={(value) => handleSelectChange('employmentStatus', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employment status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employed">Employed</SelectItem>
                    <SelectItem value="self_employed">Self-employed</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="unemployed">Unemployed</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="monthlyIncome">Monthly Income (PLN)</Label>
                <Input
                  id="monthlyIncome"
                  name="monthlyIncome"
                  type="number"
                  value={formData.monthlyIncome}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="accommodation">Accommodation Details</Label>
              <Textarea
                id="accommodation"
                name="accommodation"
                rows={3}
                value={formData.accommodation}
                onChange={handleInputChange}
                placeholder="Provide details about your accommodation in Poland"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="previousApplications">Previous Applications</Label>
              <Select 
                value={formData.previousApplications} 
                onValueChange={(value) => handleSelectChange('previousApplications', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Have you applied for Karta Pobytu before?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No, this is my first application</SelectItem>
                  <SelectItem value="yes_approved">Yes, previous application was approved</SelectItem>
                  <SelectItem value="yes_rejected">Yes, previous application was rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="criminalRecord">Criminal Record</Label>
              <Select 
                value={formData.criminalRecord} 
                onValueChange={(value) => handleSelectChange('criminalRecord', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Do you have any criminal record?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No criminal record</SelectItem>
                  <SelectItem value="yes">Yes, I have a criminal record</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="healthInsurance">Health Insurance</Label>
              <Textarea
                id="healthInsurance"
                name="healthInsurance"
                rows={2}
                value={formData.healthInsurance}
                onChange={handleInputChange}
                placeholder="Provide details about your health insurance coverage"
                required
              />
            </div>

            <div className="flex space-x-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/documents')}
                className="w-full"
              >
                Back
              </Button>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default ApplicationForm;
